import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TenantService } from './Services';
import { ProvisionedApplicationViewModel } from '@app/Tenant/Applications/Airsweb/Models/provisioned-application.view-model';
import { TenantListPageResult } from '@app/Tenant/Applications/Airsweb/Models/tenant-list-page-result';
import { NgStyle, NgClass, DatePipe } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MultiSelectComponent } from '@app/Common/multi-select/multi-select.component';
import { MultiSelectItem } from '@app/Models/multi-select-item';

interface Tab {
    label: string;
    view: string;
}

@Component({
    selector: 'my-package',
    templateUrl: './app.home.component.html',
    styleUrls: ['./app.home.component.less'],
    providers: [Title],
    imports: [NgStyle, MatButton, MatIconButton, MatIcon, MatTooltip, RouterLinkActive, RouterLink, NgClass, DatePipe, MatTabGroup, MatTab, MultiSelectComponent]
})
export class HomeComponent implements OnInit, OnDestroy {

    readonly tabs: Tab[] = [
        { label: 'Recently Visited',    view: 'recently-visited' },
        { label: 'Created by Me',       view: 'created-by-me' },
        { label: 'My Pilot Tenants',    view: 'pilot-tenants' },
        { label: 'Recently Provisioned', view: 'recently-provisioned' }
    ];

    readonly pilotTenantsView = 'pilot-tenants';

    activeTabIndex = 0;
    apps: ProvisionedApplicationViewModel[] = [];
    loading = false;
    currentPage = 1;
    totalCount = 0;
    readonly pageSize = 10;
    pilotStatusOptions: MultiSelectItem[] = [];
    selectedPilotStatuses: string[] = [];
    pilotStatusesError: string | null = null;

    get totalPages(): number {
        return Math.ceil(this.totalCount / this.pageSize);
    }

    get pageRangeStart(): number {
        return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    }

    get pageRangeEnd(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    private readonly _localStorageKey = 'act-home-tab';
    private readonly _destroy$ = new Subject<void>();
    private readonly _pageCache = new Map<string, TenantListPageResult>();
    private _pendingRequestKey: string | null = null;
    private _latestRequestKey: string | null = null;

    private _pageKey(view: string): string {
        return `act-home-page-${view}`;
    }

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly tenantService: TenantService,
        private readonly router: Router,
        private readonly title: Title
    ) {
        this.title.setTitle('EcoEHS ACT');
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this._destroy$))
            .subscribe((params) => {
                const view = params['tab']
                    ?? localStorage.getItem(this._localStorageKey)
                    ?? 'recently-visited';
                const index = this.tabs.findIndex((t) => t.view === view);
                this.activeTabIndex = Math.max(index, 0);
                const restoredView = this.tabs[this.activeTabIndex].view;
                const restoredPage = Number.parseInt(localStorage.getItem(this._pageKey(restoredView)) ?? '1', 10) || 1;
                this._loadTab(restoredView, restoredPage);
                this._loadPilotStatusPreferenceIfNeeded(restoredView);
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onTabChange(event: MatTabChangeEvent): void {
        const view = this.tabs[event.index].view;
        localStorage.setItem(this._localStorageKey, view);
        this.router.navigate([], {
            queryParams: { tab: view },
            replaceUrl: true
        });
    }

    getBackground(a: ProvisionedApplicationViewModel): object {
        return {
            'background-color': !a.ProvisioningComplete ? '#87b2f9' : ''
        };
    }

    goToPage(page: number): void {
        const view = this.tabs[this.activeTabIndex].view;
        this._loadTab(view, page);
    }

    onPilotStatusesChange(statuses: string[]): void {
        if (statuses.length === 0) {
            this.pilotStatusesError = 'At least one status is required.';
            this.selectedPilotStatuses = [...this.selectedPilotStatuses];
            return;
        }
        this.pilotStatusesError = null;
        this.selectedPilotStatuses = statuses;
        this.tenantService
            .updatePilotTenantStatuses(statuses)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this._clearCacheForView(this.pilotTenantsView);
                this._loadTab(this.pilotTenantsView, 1);
            });
    }

    private _loadPilotStatusPreferenceIfNeeded(view: string): void {
        if (view !== this.pilotTenantsView) {
            return;
        }
        this.pilotStatusesError = null;
        this.tenantService
            .getTenantStatuses()
            .pipe(takeUntil(this._destroy$))
            .subscribe((statuses) => {
                this.pilotStatusOptions = statuses.map((s) => new MultiSelectItem(s.Value, s.Text));
            });
        this.tenantService
            .getPilotTenantStatuses()
            .pipe(takeUntil(this._destroy$))
            .subscribe((statuses) => {
                this.selectedPilotStatuses = statuses;
            });
    }

    private _clearCacheForView(view: string): void {
        Array.from(this._pageCache.keys())
            .filter((key) => key.startsWith(`${view}:`))
            .forEach((key) => this._pageCache.delete(key));
    }

    private _loadTab(view: string, page = 1): void {
        const cacheKey = `${view}:${page}`;
        this._latestRequestKey = cacheKey;
        const cached = this._pageCache.get(cacheKey);
        if (cached) {
            this.apps = cached.Items;
            this.totalCount = cached.TotalCount;
            this.currentPage = page;
            this.loading = false;
            localStorage.setItem(this._pageKey(view), page.toString());
            return;
        }
        if (this._pendingRequestKey === cacheKey) {
            return;
        }
        this._pendingRequestKey = cacheKey;

        this.loading = true;
        this.apps = [];
        this.tenantService
            .getTenantList(view, page, this.pageSize)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (result) => {
                    this._pageCache.set(cacheKey, result);
                    this._clearPendingRequestKey(cacheKey);
                    // A superseded request (the user has since switched view/page) still
                    // populates the cache, but must not overwrite what's currently shown.
                    if (cacheKey !== this._latestRequestKey) {
                        return;
                    }
                    this.apps = result.Items;
                    this.totalCount = result.TotalCount;
                    this.currentPage = page;
                    localStorage.setItem(this._pageKey(view), page.toString());
                    this.loading = false;
                },
                error: () => {
                    this._clearPendingRequestKey(cacheKey);
                    if (cacheKey !== this._latestRequestKey) {
                        return;
                    }
                    this.loading = false;
                    this.totalCount = 0;
                    this.currentPage = page;
                }
            });
    }

    private _clearPendingRequestKey(cacheKey: string): void {
        if (this._pendingRequestKey === cacheKey) {
            this._pendingRequestKey = null;
        }
    }
}

